// The permissions editor, shared by a folder (in a modal) and a file (in its details pane).
//
// What it shows is the ACL IN FORCE, whether this node carries it or inherits it — the question someone
// opens this to answer is "who can see this", and an empty panel on an inherited node answers it wrongly.
// Editing an inherited node writes its first own ACL; "inherit again" removes it and the node goes back to
// following the folder above.

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { Select } from '/imports/ui/Select';
import { toastOnError } from '/imports/ui/utils';
import { useMethod } from '/imports/hooks';
import {
  DriveAccessLevel, DriveAclEntry, driveAccessLevels, drivePrincipalOptions, driveSetAcl,
} from '/imports/api/drive';
import { driveAccessLabel } from '/imports/ui/DriveShared';

const DrivePermissionsStyled = styled.div`
  .acl-row {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.3rem 0; border-bottom: 1px solid ${css.grey8}; font-size: 0.85rem;
  }
  .acl-row .who { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .acl-row select { height: 2rem; min-height: 2rem; font-size: 0.8rem; width: auto; }
  .acl-add { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.6rem; }
  .acl-add select { height: 2.2rem; min-height: 2.2rem; font-size: 0.8rem; }
  .acl-add .who { flex: 1; min-width: 0; }
  .acl-foot { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.75rem; }
  .source { font-size: 0.78rem; color: ${css.grey4}; margin-bottom: 0.4rem; display: flex; align-items: center; gap: 0.3rem; }
`;

export interface DrivePermissionsProps {
  kind: 'folder' | 'file';
  _id: string;
  acl: DriveAclEntry[];
  // Absent when the node inherits. Shown as such, because "set here" and "the same by inheritance" are
  // different answers to "what happens if the folder above changes".
  ownAcl?: DriveAclEntry[];
  canManage: boolean;
  onChanged(): void;
}

export const DrivePermissions = (props: DrivePermissionsProps) => {
  const [entries, setEntries] = useState<DriveAclEntry[]>(props.acl);
  const [adding, setAdding] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [options] = useMethod(drivePrincipalOptions, undefined, []);
  const stored = JSON.stringify(props.acl);
  // The panel is reused as the selection moves from node to node, and re-read after a save, so the edited
  // copy has to follow what came back rather than staying on whatever it was first handed.
  useEffect(() => setEntries(props.acl), [stored, props._id]);
  const own = !!props.ownAcl?.length;
  const labels = new Map((options || []).map((option) => [option.principal, option.label]));
  const dirty = JSON.stringify(entries) !== JSON.stringify(props.acl);

  const save = toastOnError(async (acl?: DriveAclEntry[]) => {
    setSaving(true);
    try {
      const counts = await driveSetAcl.call({ kind: props.kind, _id: props._id, acl });
      if (counts.folders + counts.files > 1) toast(strings.PermissionsRestamped(counts.folders, counts.files));
      props.onChanged();
    }
    finally {
      setSaving(false);
    }
  });

  const level = (principal: string, access: DriveAccessLevel) =>
    setEntries(entries.map((entry) => (entry.principal === principal ? { ...entry, access } : entry)));

  const levelOptions = driveAccessLevels.map((access) => ({ value: access, label: driveAccessLabel(access) }));
  const unused = (options || []).filter((option) => !entries.some((entry) => entry.principal === option.principal));

  return (
    <DrivePermissionsStyled>
      <div className="source">
        <Icon icon={own ? 'lock' : 'folder'} size={14} fill={css.grey4} />
        {own ? strings.PermissionsOwn() : strings.PermissionsInherited()}
      </div>
      {entries.map((entry) => (
        <div className="acl-row" key={entry.principal}>
          <span className="who">{labels.get(entry.principal) || entry.principal}</span>
          <Select
            value={entry.access}
            options={levelOptions}
            disabled={!props.canManage || saving}
            onChange={(value) => level(entry.principal, value as DriveAccessLevel)}
          />
          {props.canManage && (
            <button
              type="button"
              className="icon-btn"
              aria-label={strings.Remove()}
              onClick={() => setEntries(entries.filter((held) => held.principal !== entry.principal))}
            >
              <Icon icon="close" size={16} fill={css.grey3} />
            </button>
          )}
        </div>
      ))}
      {!entries.length && <div className="muted">{strings.NoPermissionsSet()}</div>}
      {props.canManage && (
        <>
          <div className="acl-add">
            <Select
              className="who"
              value={adding || ''}
              placeholder={strings.AddPermission()}
              options={unused.map((option) => ({ value: option.principal, label: option.label }))}
              disabled={saving}
              onChange={(value) => setAdding(value)}
            />
            <Button
              className="secondary"
              disabled={!adding || saving}
              onClick={() => {
                if (!adding) return;
                setEntries([...entries, { principal: adding, access: 'viewer' }]);
                setAdding(undefined);
              }}
            >
              {strings.Add()}
            </Button>
          </div>
          <div className="acl-foot">
            <Button disabled={!dirty || saving} onClick={() => save(entries)}>{strings.Save()}</Button>
            {dirty && <Button className="secondary" disabled={saving} onClick={() => setEntries(props.acl)}>{strings.Cancel()}</Button>}
            {own && !dirty && (
              <Button className="secondary" disabled={saving} onClick={() => save(undefined)}>{strings.PermissionsInheritAgain()}</Button>
            )}
          </div>
        </>
      )}
    </DrivePermissionsStyled>
  );
};
